import { t } from 'ember-intl';
import Component from '@glimmer/component';
import { Section } from 'tio-ui/components/layout';
import { PhChatCircle, PhArrowRight } from 'ember-phosphor-icons';
import { employeeVisibleComments } from '../../../utils/tuition-assistance/comments';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasProgramInstanceExtension as TasProgramInstanceModel } from 'tio-data/data/resources/tas-program-instance.ext';
import type { CommentExtension as CommentModel } from 'tio-data/data/resources/comment.ext';

const MAX_PREVIEW_LENGTH = 200;

interface RecentCommentPreviewSignature {
  Args: {
    /**
     * The record being approved. Course applications and program instances both
     * carry the same `comments` relationship, and both approval pages surface
     * this card, so either one is accepted.
     */
    model: TasApplicationModel | TasProgramInstanceModel;
    /** Called when the user clicks "Go to Comments"; receives the tab the comment belongs to */
    onViewMore: (tab: 'approver' | 'employee') => void;
    /**
     * Allow employee-comment previews only when the comments drawer offers that channel. Defaults
     * to true for course applications.
     */
    showEmployeeComments?: boolean;
  };
}

export default class RecentCommentPreview extends Component<RecentCommentPreviewSignature> {
  /**
   * Approver-to-approver comments:
   * Has the 'approver' tag but NOT 'Partner Messaging' or 'employee'
   */
  get approverComments(): CommentModel[] {
    return [...(this.args.model.comments ?? [])]
      .filter(
        (comment) =>
          !comment.isNew &&
          comment.tags?.some((tag) => tag === 'approver') &&
          !comment.tags?.includes('Partner Messaging') &&
          !comment.tags?.includes('employee')
      )
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  /**
   * Approver-to-employee comments, newest first. Shares `employeeVisibleComments`
   * with the comments drawer so the preview can never disagree with the tab it
   * links to about which comments are employee-facing.
   */
  get employeeComments(): CommentModel[] {
    if (this.args.showEmployeeComments === false) {
      return [];
    }
    return employeeVisibleComments(this.args.model.comments);
  }

  get totalComments(): number {
    return this.approverComments.length + this.employeeComments.length;
  }

  get mostRecentComment(): CommentModel | null {
    const all = [...this.approverComments, ...this.employeeComments].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    return all[0] ?? null;
  }

  /**
   * Which drawer tab houses the most recent comment
   */
  get commentSource(): 'approver' | 'employee' {
    if (!this.mostRecentComment) return 'approver';
    return this.employeeComments.includes(this.mostRecentComment) ? 'employee' : 'approver';
  }

  get authorName(): string {
    const comment = this.mostRecentComment;
    if (!comment) return '';
    const person = comment.person;
    if (person?.firstName && person?.lastName) {
      return `${person.firstName} ${person.lastName}`;
    }
    return person?.fullName || comment.commentor || '';
  }

  get formattedDate(): string {
    const comment = this.mostRecentComment;
    if (!comment?.createdAt) return '';
    return new Date(comment.createdAt).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  /**
   * Plain-text body of the most recent comment.
   * TAS approval responses store JSON `{ title, body }`; we extract .body for those.
   */
  get commentBody(): string {
    const comment = this.mostRecentComment;
    if (!comment) return '';
    if (comment.isTasApproval) {
      try {
        return comment.tasApprovalNote.body;
      } catch {
        return comment.comment ?? '';
      }
    }
    return comment.comment ?? '';
  }

  get isTruncated(): boolean {
    return this.commentBody.length > MAX_PREVIEW_LENGTH;
  }

  get truncatedBody(): string {
    if (this.isTruncated) {
      return this.commentBody.slice(0, MAX_PREVIEW_LENGTH) + '...';
    }
    return this.commentBody;
  }

  <template>
    {{#if this.mostRecentComment}}
      <Section>
        <:body>
          <div class="max-w-3xl tio-surface-panel" data-test-recent-comment-preview>
            <div class="tio-row-tight mb-3">
              <PhChatCircle class="tio-fg-muted w-5 h-5" />
              <h3 class="tio-text-base-semibold tio-fg-primary">
                {{t "tuition_assistance.application_approval.comments"}}
                <span class="tio-fg-muted font-normal">({{this.totalComments}})</span>
              </h3>
            </div>

            <p class="tio-text-caption font-semibold uppercase tracking-wide mb-3">
              {{t "tuition_assistance.application_approval.most_recent"}}
            </p>

            <div class="tio-row-tight mb-1">
              <span class="tio-text-sm-semibold tio-fg-primary">{{this.authorName}}</span>
              <span class="tio-text-caption">{{this.formattedDate}}</span>
            </div>

            <div class="tio-flex-between items-end gap-4">
              <p class="text-sm tio-fg-secondary leading-relaxed">
                {{this.truncatedBody}}
              </p>

              <button
                type="button"
                class="shrink-0 tio-inline-row-extra-tight tio-text-sm-medium tio-fg-muted hover:tio-fg-primary transition-colors focus:outline-none"
                {{on "click" (fn @onViewMore this.commentSource)}}
                data-test-go-to-comments
              >
                {{t "tuition_assistance.application_approval.go_to_comments"}}
                <PhArrowRight class="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </:body>
      </Section>
    {{/if}}
  </template>
}
